import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
  Paper,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SelectChangeEvent,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { emailTemplateService } from './emailTemplate.service';
import type { EmailTemplate } from './emailTemplate.dto';
import RichTextEditor from '../../components/RichTextEditor';
import { formatDate } from '@/helpers';

const emailTemplateStyles = `
  body {
    margin: 0;
    padding: 20px;
    font-family: Arial, sans-serif;
    background-color: #f4f4f4;
  }
  .preview-container {
    max-width: 1200px;
    margin: 0 auto;
  }
  .preview-title {
    text-align: center;
    color: #121B31;
    margin-bottom: 30px;
  }
  .template-section {
    margin-bottom: 40px;
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  .template-name {
    color: #121B31;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    border-bottom: 2px solid #D0E156;
    padding-bottom: 5px;
  }
  .email-container {
    max-width: 600px;
    margin: 0 auto;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
  }
  .header {
    background-color: #121B31;
    padding: 20px;
    text-align: center;
  }
  .logo {
    color: #ffffff;
    font-size: 24px;
    font-weight: bold;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
  .logo-image {
    height: 40px;
    width: auto;
    max-width: 200px;
  }
  .divider {
    height: 3px;
    background-color: #D0E156;
    margin: 0;
  }
  .content {
    padding: 30px;
  }
  .title {
    color: #121B31;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
    text-align: center;
  }
  .greeting {
    color: #374151;
    font-size: 16px;
    margin-bottom: 15px;
  }
  .message {
    color: #374151;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 15px;
  }
  .otp-container {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
  }
  .otp-code {
    font-size: 32px;
    font-weight: bold;
    color: #121B31;
    letter-spacing: 5px;
    margin: 10px 0;
  }
  .otp-label {
    color: #FFFFFF;
    font-size: 14px;
    margin-bottom: 10px;
  }
  .otp-note {
    color: #FFFFFF;
    font-size: 12px;
    margin-top: 10px;
  }
  .reset-button {
    display: inline-block;
    background-color: #121B31;
    color: #ffffff;
    padding: 15px 30px;
    text-decoration: none;
    border-radius: 8px;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
    transition: background-color 0.3s ease;
  }
  .reset-button:hover {
    background-color: #1e40af;
  }
  .reset-url {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
    color: #FFFFFF;
  }
  .reset-note {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0;
    color: #92400e;
    font-size: 14px;
  }
  .button-container {
    text-align: center;
    margin: 30px 0;
    color: #FFFFFF;
  }
  .footer {
    background-color: #ffffff;
    padding: 20px;
    text-align: center;
  }
  .contact-info {
    color: #121B31;
    font-size: 12px;
    line-height: 1.5;
  }
  .disclaimer {
    background-color: #121B31;
    color: #FFFFFF;
    padding: 15px;
    text-align: center;
    font-size: 12px;
  }
  .closing {
    color: #374151;
    font-size: 16px;
    margin-top: 20px;
  }

  .invoice-section {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }
  .invoice-title {
    color: #121B31;
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 20px;
    text-align: center;
  }
  .invoice-table {
    width: 100%;
    border-collapse: collapse;
    margin: 20px 0;
    background-color: #f9fafb;
    border-radius: 8px;
    overflow: hidden;
  }
  .invoice-table tr {
    border-bottom: 1px solid #e5e7eb;
  }
  .invoice-table tr:last-child {
    border-bottom: none;
  }
  .invoice-table td {
    padding: 12px 15px;
    color: #374151;
    font-size: 14px;
  }
  .invoice-table td:first-child {
    font-weight: 600;
    color: #121B31;
    width: 40%;
  }
  .invoice-table td:last-child {
    text-align: right;
    color: #121B31;
  }
  .invoice-total-row {
    background-color: #121B31 !important;
    font-weight: bold;
  }
  .invoice-total-row td {
    color: #ffffff !important;
    font-size: 16px;
    padding: 15px;
  }
  .booking-details {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }
  .booking-title {
    color: #121B31;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    border-bottom: 2px solid #D0E156;
    padding-bottom: 8px;
  }
  .booking-item {
    color: #374151;
    font-size: 14px;
    margin: 10px 0;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
  }
  .booking-item:last-child {
    border-bottom: none;
  }
  .booking-item strong {
    color: #121B31;
    display: inline-block;
    width: 120px;
  }
  .ck-content figure.table:not(.layout-table) {
    display: table;
    width: 100%;
  }

  .reset-button {
    display: inline-block;
    background-color: #1e3a8a;
    color: #ffffff;
    padding: 15px 30px;
    text-decoration: none;
    border-radius: 8px;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
    transition: background-color 0.3s ease;
  }
  .reset-button:hover {
    background-color: #1e40af;
  }
  .reset-note {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0;
    color: #92400e;
    font-size: 14px;
  }
  .reset-url {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
    color: #6b7280;
  }
  .button-container {
    text-align: center;
    margin: 30px 0;
  }

  .reset-button {
    display: inline-block;
    background-color: #1e3a8a;
    color: #ffffff;
    padding: 15px 30px;
    text-decoration: none;
    border-radius: 8px;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
    transition: background-color 0.3s ease;
  }
  .reset-button:hover {
    background-color: #1e40af;
  }
  .reset-note {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0;
    color: #92400e;
    font-size: 14px;
  }
  .reset-url {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
    color: #6b7280;
  }
  .button-container {
    text-align: center;
    margin: 30px 0;
  }
    
  .password-box {
    background-color: #f9fafb;
    border: 2px solid #D0E156;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
    text-align: center;
  }
  .password-label {
    color: #121B31;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }
  .password-code {
    font-size: 24px;
    font-weight: bold;
    color: #121B31;
    font-family: 'Courier New', monospace;
    letter-spacing: 3px;
    padding: 15px;
    background-color: #ffffff;
    border-radius: 6px;
    margin: 10px 0;
  }
  .download-button {
    display: inline-block;
    background-color: #121B31;
    color: #ffffff !important;
    padding: 15px 30px;
    text-decoration: none;
    border-radius: 8px;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
  }
  .download-button:hover {
    background-color: #1e40af;
  }
  .instructions-box {
    background-color: #fef3c7;
    border-left: 4px solid #f59e0b;
    border-radius: 4px;
    padding: 15px;
    margin: 20px 0;
    color: #92400e;
    font-size: 14px;
  }
  .booking-details {
    background-color: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    margin: 20px 0;
  }
  .booking-title {
    color: #121B31;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    border-bottom: 2px solid #D0E156;
    padding-bottom: 8px;
  }
  .booking-item {
    color: #374151;
    font-size: 14px;
    margin: 10px 0;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
  }
  .booking-item:last-child {
    border-bottom: none;
  }
  .booking-item strong {
    color: #121B31;
    display: inline-block;
    width: 120px;
  }
`;

const EditEmailTemplate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [formData, setFormData] = useState({
    emailType: '',
    subject: '',
    fromEmail: '',
    note: '',
    content: '',
    createdAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  // Set up logout callback and fetch data
  useEffect(() => {
    emailTemplateService.setLogoutCallback(logout);

    const fetchData = async () => {
      if (!token || !id) return;

      try {
        setLoading(true);
        const template = await emailTemplateService.getEmailTemplate(
          token,
          parseInt(id)
        );

        console.log('Fetched email template data:', template);

        setFormData({
          emailType: template.emailType || '',
          subject: template.subject || '',
          fromEmail: template.fromEmail || '',
          note: template.note || '',
          content: template.content || '',
          createdAt: template.createdAt || '',
        });
      } catch (err) {
        if (err instanceof Error) {
          setSnackbar({
            open: true,
            message: err.message,
            severity: 'error',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to fetch email template',
            severity: 'error',
          });
        }
        navigate('/email-templates');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, id, navigate, logout]);

  const handleChange =
    (field: keyof typeof formData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent
    ) => {
      const value: string | number | boolean = (
        event.target as HTMLInputElement
      ).value;

      setFormData(prev => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.emailType.trim()) {
      newErrors.emailType = 'Email type is required';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.fromEmail.trim()) {
      newErrors.fromEmail = 'From email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail.trim())) {
      newErrors.fromEmail = 'Enter a valid email';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !token || !id) return;

    setSaving(true);
    try {
      const templateData: Partial<EmailTemplate> = {
        emailType: formData.emailType,
        subject: formData.subject,
        fromEmail: formData.fromEmail,
        content: formData.content,
      };

      await emailTemplateService.updateEmailTemplate(
        token,
        parseInt(id),
        templateData
      );

      setSnackbar({
        open: true,
        message: 'Email template updated successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/email-templates', {
          state: {
            message: 'Email template updated successfully',
            severity: 'success',
          },
        });
      }, 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : 'Failed to update email template',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/email-templates');
  };

  const handleDelete = async () => {
    if (!token || !id) return;

    try {
      await emailTemplateService.deleteEmailTemplate(token, parseInt(id));
      setSnackbar({
        open: true,
        message: 'Email template deleted successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/email-templates');
      }, 1500);
    } catch (err) {
      if (err instanceof Error) {
        setSnackbar({
          open: true,
          message: err.message,
          severity: 'error',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete email template',
          severity: 'error',
        });
      }
    }
    setDeleteDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading content data...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <style>{emailTemplateStyles}</style>
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/email-templates')}
                sx={{ mr: 2 }}
              >
                <ArrowBack />
              </IconButton>
              <Edit color="primary" sx={{ mr: 1 }} />
              <Typography variant="h4" component="h1">
                Edit Email Template
              </Typography>
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Update email template
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          {/* Settings Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Template Settings
                </Typography>
                {/* <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={String(formData.status)}
                    label="Status"
                    onChange={handleChange('status')}
                  >
                    {statuses.map(status => (
                      <MenuItem
                        key={String(status.value)}
                        value={String(status.value)}
                      >
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}
                {formData.createdAt && (
                  <>
                    {/* <Divider sx={{ my: 2 }} /> */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Created on
                    </Typography>
                    <Typography variant="h6">
                      {formatDate(formData.createdAt)}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Template Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Type"
                        value={formData.emailType}
                        onChange={handleChange('emailType')}
                        error={!!errors.emailType}
                        helperText={errors.emailType}
                        required
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Subject"
                        value={formData.subject}
                        onChange={handleChange('subject')}
                        error={!!errors.subject}
                        helperText={errors.subject}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="From Email"
                        value={formData.fromEmail}
                        onChange={handleChange('fromEmail')}
                        error={!!errors.fromEmail}
                        helperText={errors.fromEmail || ' '}
                        required
                      >
                        <MenuItem value="admin@clearscan.ca">
                          admin@clearscan.ca
                        </MenuItem>
                        <MenuItem value="noreply@clearscan.ca">
                          noreply@clearscan.ca
                        </MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          color: errors.content ? '#d32f2f' : 'text.secondary',
                        }}
                      >
                        Email Content *
                      </Typography>
                      <Typography
                        variant="caption"
                        color="#d32f2f"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Note: You can use dynamic keys such as{' '}
                        <strong>{formData.note}</strong> in the content.
                      </Typography>
                      <RichTextEditor
                        value={formData.content}
                        onChange={value =>
                          setFormData({ ...formData, content: value })
                        }
                        placeholder="Enter the email content here..."
                        error={!!errors.content}
                      />
                      {errors.content && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#d32f2f', mt: 0.5, ml: 1.75 }}
                        >
                          {errors.content}
                        </Typography>
                      )}
                    </Grid>

                    {/* SEO Information */}
                    {/* <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        SEO Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Description"
                        multiline
                        rows={3}
                        value={formData.metaDescription}
                        onChange={handleChange('metaDescription')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Meta Keywords"
                        value={formData.metaKeywords}
                        onChange={handleChange('metaKeywords')}
                      />
                    </Grid> */}
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      startIcon={<Cancel />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={
                        saving ? <CircularProgress size={20} /> : <Save />
                      }
                      disabled={saving}
                      sx={{
                        background:
                          'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                        '&:hover': {
                          background:
                            'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                        },
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Email Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{formData.emailType}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditEmailTemplate;
